import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Budgets() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState({ overall: 0, categories: {} });
  const [spent, setSpent] = useState({ total: 0, by_category: {} });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const month = new Date().toISOString().slice(0, 7);
    const [cats, b, s] = await Promise.all([
      api.get("/categories"),
      api.get("/budgets"),
      api.get("/summary", { params: { month } }),
    ]);
    setCategories(cats.data);
    setBudgets({ overall: b.data.overall || 0, categories: { ...Object.fromEntries(cats.data.map((c) => [c, 0])), ...(b.data.categories || {}) } });
    setSpent({ total: s.data.total, by_category: s.data.by_category });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/budgets", {
        overall: parseFloat(budgets.overall) || 0,
        categories: Object.fromEntries(
          Object.entries(budgets.categories).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
      });
      toast.success("Budgets saved");
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="budgets-page">
      <div>
        <div className="overline">LIMITS</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Budgets</h1>
        <p className="font-mono text-sm text-[var(--text-secondary)] mt-2">Set monthly caps. The dashboard turns red when you exceed them.</p>
      </div>

      <div className="bg-white border-2 border-black p-6">
        <div className="overline mb-2">OVERALL MONTHLY BUDGET</div>
        <div className="flex items-baseline gap-4 flex-wrap">
          <input
            data-testid="overall-budget-input"
            type="number" min="0" step="0.01"
            value={budgets.overall}
            onChange={(e) => setBudgets({ ...budgets, overall: e.target.value })}
            className="bg-transparent border-b-2 border-black focus:border-[var(--alert-red)] outline-none py-2 text-4xl font-display font-black tracking-tighter w-64"
          />
          <span className="font-mono text-sm text-[var(--text-secondary)]">{currency}</span>
        </div>
        <div className="font-mono text-sm mt-3">
          Spent: <span className={`font-bold ${spent.total > (parseFloat(budgets.overall) || 0) && budgets.overall > 0 ? "text-[var(--alert-red)]" : ""}`}>{fmtMoney(spent.total, currency)}</span>
          {" / "}
          {fmtMoney(parseFloat(budgets.overall) || 0, currency)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((cat) => {
          const v = budgets.categories[cat] || 0;
          const used = spent.by_category[cat] || 0;
          const over = parseFloat(v) > 0 && used > parseFloat(v);
          const pct = parseFloat(v) > 0 ? Math.min(100, Math.round((used / parseFloat(v)) * 100)) : 0;
          return (
            <div
              key={cat}
              data-testid={`budget-card-${cat}`}
              className={`p-5 bg-white border ${over ? "border-2 border-[var(--alert-red)] bg-[var(--alert-wash)]" : "border-black/15"}`}
            >
              <div className="flex justify-between items-baseline">
                <div className="font-display font-bold text-lg">{cat}</div>
                {over && <div className="overline text-[var(--alert-red)]">OVER!</div>}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  data-testid={`budget-input-${cat}`}
                  type="number" min="0" step="0.01"
                  value={budgets.categories[cat] ?? 0}
                  onChange={(e) => setBudgets({ ...budgets, categories: { ...budgets.categories, [cat]: e.target.value } })}
                  className="flex-1 bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-1 font-mono text-lg"
                />
                <span className="font-mono text-xs text-[var(--text-secondary)]">{currency}</span>
              </div>
              <div className="mt-3 h-2 bg-black/10">
                <div
                  className={`h-full ${over ? "bg-[var(--alert-red)]" : "bg-black"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-xs text-[var(--text-secondary)]">
                {fmtMoney(used, currency)} used {parseFloat(v) > 0 && `· ${pct}%`}
              </div>
            </div>
          );
        })}
      </div>

      <button
        data-testid="save-budgets"
        onClick={save}
        disabled={saving}
        className="bg-black text-white px-8 py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50"
      >
        {saving ? "SAVING…" : "SAVE BUDGETS →"}
      </button>
    </div>
  );
}
